import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const BusinessInfoSettings = () => {
  const theme = useThemeClasses();
  const [info, setInfo] = useState({
    business_name: '',
    business_type: 'online_store',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    province: '',
    postal_code: '',
    country: 'Canada',
    tax_number: '',
    tax_rate: 13,
    currency: 'CAD',
    currency_symbol: '$',
    timezone: 'America/Toronto',
    date_format: 'YYYY-MM-DD',
    time_format: '12h',
    business_hours: {
      monday: { open: '09:00', close: '17:00', closed: false },
      tuesday: { open: '09:00', close: '17:00', closed: false },
      wednesday: { open: '09:00', close: '17:00', closed: false },
      thursday: { open: '09:00', close: '17:00', closed: false },
      friday: { open: '09:00', close: '17:00', closed: false },
      saturday: { open: '10:00', close: '16:00', closed: false },
      sunday: { open: '', close: '', closed: true }
    },
    social_media: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: ''
    },
    additional_info: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    loadBusinessInfo();
  }, []);

  const loadBusinessInfo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/business-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInfo({ ...info, ...response.data });
    } catch (error) {
      console.error('Failed to load business info:', error);
      toast.error('Failed to load business information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/api/admin/business-info`, info, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business information saved successfully!');
    } catch (error) {
      console.error('Failed to save business info:', error);
      toast.error('Failed to save information');
    } finally {
      setSaving(false);
    }
  };

  const updateBusinessHours = (day, field, value) => {
    setInfo({
      ...info,
      business_hours: {
        ...info.business_hours,
        [day]: {
          ...info.business_hours[day],
          [field]: value
        }
      }
    });
  };

  const updateSocialMedia = (platform, value) => {
    setInfo({
      ...info,
      social_media: {
        ...info.social_media,
        [platform]: value
      }
    });
  };

  if (loading) return <div className="p-8">Loading business information...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>🏢 Business Information</h2>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>Configure your business details and operating hours</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Tabs */}
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          {[
            { id: 'basic', label: 'Basic Info', icon: '📋' },
            { id: 'contact', label: 'Contact & Address', icon: '📍' },
            { id: 'hours', label: 'Business Hours', icon: '🕐' },
            { id: 'regional', label: 'Regional Settings', icon: '🌍' },
            { id: 'social', label: 'Social Media', icon: '📱' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} ${theme.isDark ? 'hover:text-gray-200' : 'hover:text-blue-600'}`
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Basic Info Tab */}
      {activeTab === 'basic' && (
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className="text-lg font-semibold mb-4">Business Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Business Name *</label>
                <input
                  type="text"
                  value={info.business_name || ''}
                  onChange={(e) => setInfo({ ...info, business_name: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder="RG-3D"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Business Type</label>
                <select
                  value={info.business_type || `online_store`}
                  onChange={(e) => setInfo({ ...info, business_type: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="online_store">Online Store</option>
                  <option value="retail">Retail Store</option>
                  <option value="service">Service Business</option>
                  <option value="manufacturer">Manufacturer</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tax Number / Business Number</label>
                <input
                  type="text"
                  value={info.tax_number || ''}
                  onChange={(e) => setInfo({ ...info, tax_number: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder="123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Default Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={info.tax_rate || 0}
                  onChange={(e) => setInfo({ ...info, tax_rate: parseFloat(e.target.value) })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder="13.00"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Ontario HST is 13%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact & Address Tab */}
      {activeTab === `contact` && (
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  value={info.email || ''}
                  onChange={(e) => setInfo({ ...info, email: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder="contact@rg3d.ca"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={info.phone || ''}
                  onChange={(e) => setInfo({ ...info, phone: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className="text-lg font-semibold mb-4">Business Address</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Address Line 1</label>
                <input
                  type="text"
                  value={info.address_line1 || ''}
                  onChange={(e) => setInfo({ ...info, address_line1: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder="123 Main Street"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Address Line 2</label>
                <input
                  type="text"
                  value={info.address_line2 || ''}
                  onChange={(e) => setInfo({ ...info, address_line2: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder="Suite 100"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={info.city || ''}
                    onChange={(e) => setInfo({ ...info, city: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                    placeholder="Cambridge"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Province/State</label>
                  <input
                    type="text"
                    value={info.province || ''}
                    onChange={(e) => setInfo({ ...info, province: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                    placeholder="Ontario"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Postal Code</label>
                  <input
                    type="text"
                    value={info.postal_code || ''}
                    onChange={(e) => setInfo({ ...info, postal_code: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                    placeholder="N1R 5S2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Country</label>
                <select
                  value={info.country || `Canada`}
                  onChange={(e) => setInfo({ ...info, country: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="Canada">Canada</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Business Hours Tab */}
      {activeTab === `hours` && (
        <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
          <h3 className="text-lg font-semibold mb-4">Operating Hours</h3>
          <div className="space-y-3">
            {Object.keys(info.business_hours || {}).map((day) => (
              <div key={day} className={`flex items-center gap-4 p-3 ${theme.bgSecondary} rounded-lg`}>
                <div className="w-24">
                  <span className="font-medium capitalize">{day}</span>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!info.business_hours[day]?.closed}
                    onChange={(e) => updateBusinessHours(day, 'closed', !e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm">Open</span>
                </label>
                {!info.business_hours[day]?.closed && (
                  <>
                    <input
                      type="time"
                      value={info.business_hours[day]?.open || '09:00'}
                      onChange={(e) => updateBusinessHours(day, 'open', e.target.value)}
                      className={`px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                    />
                    <span className={`${theme.textSecondary}`}>to</span>
                    <input
                      type="time"
                      value={info.business_hours[day]?.close || '17:00'}
                      onChange={(e) => updateBusinessHours(day, 'close', e.target.value)}
                      className={`px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                    />
                  </>
                )}
                {info.business_hours[day]?.closed && (
                  <span className={`${theme.textSecondary}`}>Closed</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regional Settings Tab */}
      {activeTab === `regional` && (
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className="text-lg font-semibold mb-4">Currency & Format</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Currency</label>
                <select
                  value={info.currency || `CAD`}
                  onChange={(e) => setInfo({ ...info, currency: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Currency Symbol</label>
                <input
                  type="text"
                  value={info.currency_symbol || `$`}
                  onChange={(e) => setInfo({ ...info, currency_symbol: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder="$"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Timezone</label>
                <select
                  value={info.timezone || `America/Toronto`}
                  onChange={(e) => setInfo({ ...info, timezone: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="America/Toronto">Eastern Time (Toronto)</option>
                  <option value="America/Vancouver">Pacific Time (Vancouver)</option>
                  <option value="America/Chicago">Central Time (Chicago)</option>
                  <option value="America/New_York">Eastern Time (New York)</option>
                  <option value="America/Los_Angeles">Pacific Time (Los Angeles)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Time Format</label>
                <select
                  value={info.time_format || `12h`}
                  onChange={(e) => setInfo({ ...info, time_format: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="12h">12-hour (3:00 PM)</option>
                  <option value="24h">24-hour (15:00)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Tab */}
      {activeTab === `social` && (
        <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
          <h3 className="text-lg font-semibold mb-4">Social Media Links</h3>
          <div className="space-y-4">
            {[
              { platform: 'facebook', icon: '📘', label: 'Facebook', placeholder: 'https://facebook.com/yourpage' },
              { platform: 'instagram', icon: '📷', label: 'Instagram', placeholder: 'https://instagram.com/yourprofile' },
              { platform: 'twitter', icon: '🐦', label: 'Twitter', placeholder: 'https://twitter.com/yourhandle' },
              { platform: 'linkedin', icon: '💼', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/yourcompany' },
              { platform: 'youtube', icon: '📹', label: 'YouTube', placeholder: 'https://youtube.com/c/yourchannel' }
            ].map(({ platform, icon, label, placeholder }) => (
              <div key={platform}>
                <label className="block text-sm font-medium mb-2">{icon} {label}</label>
                <input
                  type="url"
                  value={info.social_media?.[platform] || ''}
                  onChange={(e) => updateSocialMedia(platform, e.target.value)}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500`}
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Button at Bottom */}
      <div className={`flex justify-end pt-4 border-t ${theme.border}`}>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium"
        >
          {saving ? 'Saving...' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
};